"use client";

import { useId } from "react";
import { ActivityScenePreview } from "./reference-preview-primitives";

export type ScubaDivingDisplayPreviewProps = {
  participants: Array<{
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }>;
};

/*
 * Ten metres down on a reef wall: sun shafts from the surface, a diver with a
 * yellow tank finning across the middle (the focal point) and trailing
 * bubbles, a fish school, and reef on both flanks so the sandy floor under the
 * bottom-centre participant pill stays open.
 *
 * Motion: the shafts breathe and five bubbles rise — HTML/CSS, transform and
 * opacity only, off under reduced motion.
 */
const SCUBA_KEYFRAMES = `
@keyframes scuba-rise {
  0% { transform: translate3d(0, 0, 0) scale(0.7); opacity: 0 }
  12% { opacity: 0.9 }
  100% { transform: translate3d(6px, -64px, 0) scale(1.15); opacity: 0 }
}
@keyframes scuba-shafts { 0%, 100% { opacity: 0.65 } 50% { opacity: 1 } }
.scuba-bubble { animation: scuba-rise 3.6s ease-out infinite }
.scuba-shafts { animation: scuba-shafts 6s ease-in-out infinite }
@media (prefers-reduced-motion: reduce) {
  .scuba-bubble { animation: none; opacity: 0.6 }
  .scuba-shafts { animation: none }
}
`;

const BUBBLES = [
  { size: 6, x: 0, y: 0, delay: "0s" },
  { size: 4, x: 5, y: -14, delay: "0.7s" },
  { size: 7, x: -3, y: -26, delay: "1.4s" },
  { size: 3, x: 6, y: -36, delay: "2.1s" },
  { size: 5, x: 1, y: -46, delay: "2.8s" },
];

function useSvgIds() {
  const uid = useId().replace(/[^\w-]/g, "");
  return (name: string) => `scuba-${uid}-${name}`;
}

type IdFn = ReturnType<typeof useSvgIds>;

function SceneDefs({ id }: { id: IdFn }) {
  return (
    <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id("shaft")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#c8f4ff" stopOpacity="0.34" />
          <stop offset="0.6" stopColor="#8fe3ff" stopOpacity="0.08" />
          <stop offset="1" stopColor="#8fe3ff" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={id("sand")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#1f6a7a" />
          <stop offset="1" stopColor="#0a3140" />
        </linearGradient>
        <linearGradient id={id("tank")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#ffe27a" />
          <stop offset="0.5" stopColor="#f2b51e" />
          <stop offset="1" stopColor="#9a6a06" />
        </linearGradient>
        <linearGradient id={id("glass")} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#bff4ff" />
          <stop offset="1" stopColor="#1b7393" />
        </linearGradient>
        <linearGradient id={id("rock")} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#0b3a4f" />
          <stop offset="1" stopColor="#031621" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** Surface, sun shafts, distant reef and sand — stretches with the card. */
function Water({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute inset-0 h-full w-full"
      viewBox="0 0 300 220"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {/* Surface ripples */}
      <path
        d="M0 4 Q18 0 36 4 T72 4 T108 4 T144 4 T180 4 T216 4 T252 4 T288 4 T324 4"
        stroke="#e6fbff"
        strokeOpacity="0.45"
        strokeWidth="1.2"
        fill="none"
      />
      <path
        d="M0 10 Q22 6 44 10 T88 10 T132 10 T176 10 T220 10 T264 10 T308 10"
        stroke="#e6fbff"
        strokeOpacity="0.2"
        strokeWidth="1"
        fill="none"
      />
      {/* Sun shafts */}
      <g className="scuba-shafts">
        <polygon
          points="62,0 84,0 132,180 92,180"
          fill={`url(#${id("shaft")})`}
        />
        <polygon
          points="118,0 132,0 168,170 140,170"
          fill={`url(#${id("shaft")})`}
        />
        <polygon
          points="168,0 196,0 240,185 196,185"
          fill={`url(#${id("shaft")})`}
        />
        <polygon
          points="226,0 238,0 280,160 258,160"
          fill={`url(#${id("shaft")})`}
        />
        <polygon
          points="16,0 30,0 58,150 36,150"
          fill={`url(#${id("shaft")})`}
        />
      </g>
      {/* Hazy distant reef */}
      <path
        d="M0 160 Q20 146 40 152 Q62 138 86 150 Q110 144 128 156 Q160 150 186 158 Q214 140 240 150 Q268 142 300 152 L300 220 L0 220 Z"
        fill="#0a4a63"
        opacity="0.75"
      />
      {/* Sand */}
      <path
        d="M0 182 Q60 172 150 176 Q240 172 300 182 L300 220 L0 220 Z"
        fill={`url(#${id("sand")})`}
      />
      <path
        d="M40 196 Q70 192 100 196 M170 200 Q205 195 240 200 M110 210 Q150 205 190 210"
        stroke="#7fd0d8"
        strokeOpacity="0.18"
        strokeWidth="1"
        fill="none"
      />
      {/* Marine snow */}
      {[
        [30, 60],
        [96, 40],
        [210, 70],
        [256, 34],
        [150, 128],
        [70, 118],
        [276, 112],
        [190, 112],
      ].map(([x, y]) => (
        <rect
          key={`${x}-${y}`}
          x={x}
          y={y}
          width="1.2"
          height="1.2"
          fill="#d8f6ff"
          opacity="0.45"
        />
      ))}
    </svg>
  );
}

function Diver({ id }: { id: IdFn }) {
  const suit = "#06161f";
  return (
    <svg
      className="absolute top-[74px] left-[calc(50%-58px)]"
      width="132"
      height="56"
      viewBox="0 0 132 56"
      aria-hidden="true"
      focusable="false"
    >
      {/* Fins */}
      <path d="M101 26 L127 15 Q132 21 128 27 L102 32 Z" fill="#f2c230" />
      <path d="M99 33 L125 39 Q126 46 120 46 L98 38 Z" fill="#c99a14" />
      {/* Legs */}
      <path
        d="M76 29 L91 26 L103 29"
        stroke={suit}
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M76 33 L90 37 L100 35"
        stroke="#030c12"
        strokeWidth="7"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Torso */}
      <path
        d="M29 27 Q33 19 50 20 L79 24 Q86 30 79 36 L50 38 Q33 38 29 32 Z"
        fill={suit}
      />
      {/* Tank + valve */}
      <rect
        x="43"
        y="11"
        width="35"
        height="10"
        rx="5"
        fill={`url(#${id("tank")})`}
      />
      <rect
        x="45"
        y="12.5"
        width="30"
        height="1.6"
        rx="0.8"
        fill="#fff6c8"
        opacity="0.8"
      />
      <rect x="38.5" y="13" width="6" height="6" rx="1.5" fill="#1d2a30" />
      {/* Hose */}
      <path
        d="M40 17 Q28 22 21 32"
        stroke="#0e0e10"
        strokeWidth="1.6"
        fill="none"
      />
      {/* Folded arm */}
      <path
        d="M38 33 L45 41 L57 40"
        stroke="#030c12"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Weight belt */}
      <rect x="71" y="24.5" width="4" height="12" rx="1" fill="#2a2f33" />
      {/* Head, mask, regulator */}
      <circle cx="23" cy="27" r="7.5" fill={suit} />
      <rect
        x="13.5"
        y="22"
        width="8.5"
        height="7"
        rx="2"
        fill={`url(#${id("glass")})`}
      />
      <rect
        x="15"
        y="23"
        width="2.2"
        height="3"
        rx="1"
        fill="#fff"
        opacity="0.85"
      />
      <circle cx="19" cy="33" r="2.6" fill="#11181c" />
      {/* Rim light from the surface */}
      <path
        d="M17 20.5 Q23 18 29 22 Q34 19 43 19.5 M79 24.5 L92 25.5 L103 28.5"
        stroke="#8fe3ff"
        strokeOpacity="0.65"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  );
}

function FishSchool() {
  const fish = [
    [0, 10],
    [14, 4],
    [16, 16],
    [30, 9],
    [32, 22],
    [46, 14],
    [48, 2],
  ];
  return (
    <svg
      className="absolute top-[40px] right-[6%]"
      width="64"
      height="30"
      viewBox="0 0 64 30"
      aria-hidden="true"
      focusable="false"
    >
      {fish.map(([x, y]) => (
        <path
          key={`${x}-${y}`}
          transform={`translate(${x} ${y})`}
          d="M0 4 Q5 0 10 3.4 L14 0.6 L13 4 L14 7.4 L10 4.6 Q5 8 0 4 Z"
          fill="#b6ecfa"
          opacity="0.7"
        />
      ))}
    </svg>
  );
}

function LeftReef({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute bottom-0 left-0"
      width="112"
      height="128"
      viewBox="0 0 112 128"
      aria-hidden="true"
      focusable="false"
    >
      {/* Sea fan */}
      <path
        d="M34 76 C 6 60, 2 26, 18 12 C 30 2, 50 4, 60 16 C 72 30, 62 58, 34 76 Z"
        fill="#c2456a"
        opacity="0.9"
      />
      <path
        d="M34 76 L12 30 M34 76 L22 12 M34 76 L38 8 M34 76 L54 14 M34 76 L64 34 M20 40 L44 36 M18 54 L50 52 M24 24 L52 24"
        stroke="#7a1e3d"
        strokeWidth="1.1"
        opacity="0.7"
        fill="none"
      />
      <path
        d="M58 16 C 70 30, 62 58, 34 76"
        stroke="#ffb3c5"
        strokeOpacity="0.7"
        strokeWidth="1.2"
        fill="none"
      />
      {/* Tube sponges */}
      <path d="M4 74 V52 Q8 48 12 52 V76 Z" fill="#7c4bb6" />
      <path d="M13 76 V60 Q17 56 21 60 V78 Z" fill="#9a66d4" />
      <ellipse cx="8" cy="52" rx="4" ry="1.6" fill="#2a1240" />
      <ellipse cx="17" cy="60" rx="4" ry="1.6" fill="#2a1240" />
      {/* Staghorn coral */}
      <path
        d="M76 98 L72 72 L62 56 M72 72 L82 54 M76 98 L88 78 L96 64 M88 78 L86 62"
        stroke="#ef8a5a"
        strokeWidth="4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M73 72 L83 55 M89 78 L97 65"
        stroke="#ffd0a8"
        strokeOpacity="0.7"
        strokeWidth="1.2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Rock */}
      <path
        d="M0 128 L0 72 Q14 62 28 72 Q42 66 54 82 Q70 80 80 96 Q96 100 112 128 Z"
        fill={`url(#${id("rock")})`}
      />
      <path
        d="M0 72 Q14 62 28 72 Q42 66 54 82 Q70 80 80 96"
        stroke="#5fc8e0"
        strokeOpacity="0.35"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  );
}

function RightReef({ id }: { id: IdFn }) {
  return (
    <svg
      className="absolute right-0 bottom-0"
      width="104"
      height="112"
      viewBox="0 0 104 112"
      aria-hidden="true"
      focusable="false"
    >
      {/* Sea whips */}
      <path
        d="M30 74 Q22 40 14 8 M36 74 Q40 44 30 16 M42 76 Q52 52 50 26"
        stroke="#f06a4f"
        strokeWidth="2.2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Anemone */}
      {[-60, -40, -20, 0, 20, 40, 60].map((deg) => (
        <path
          key={deg}
          d="M80 66 Q78 54 80 44"
          transform={`rotate(${deg} 80 66)`}
          stroke="#ff9fb4"
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
        />
      ))}
      {/* Rock */}
      <path
        d="M0 112 Q6 80 28 74 Q42 60 62 66 Q82 58 104 66 L104 112 Z"
        fill={`url(#${id("rock")})`}
      />
      {/* Brain coral */}
      <ellipse cx="54" cy="72" rx="17" ry="11" fill="#d9a441" />
      <path
        d="M40 72 q4 -5 8 0 t8 0 t8 0 M43 66 q4 -3 8 0 t8 0 M44 78 q4 3 8 0 t8 0"
        stroke="#8a5d14"
        strokeWidth="1.1"
        fill="none"
      />
      <path
        d="M40 66 Q52 58 68 66"
        stroke="#ffe3a3"
        strokeOpacity="0.8"
        strokeWidth="1.2"
        fill="none"
      />
      <path
        d="M0 112 Q6 80 28 74 Q42 60 62 66"
        stroke="#5fc8e0"
        strokeOpacity="0.3"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  );
}

export function ScubaDivingDisplayPreview({
  participants,
}: ScubaDivingDisplayPreviewProps) {
  const id = useSvgIds();

  return (
    <ActivityScenePreview
      label="Scuba Diving"
      backgroundClassName="bg-[#03304a]"
      participants={participants}
      scene={
        <>
          <style href="scuba-scene-keyframes" precedence="default">
            {SCUBA_KEYFRAMES}
          </style>
          <SceneDefs id={id} />

          <div className="absolute inset-0 bg-[linear-gradient(180deg,#1590b5_0%,#0a6688_26%,#05415e_62%,#02202f_100%)]" />
          {/* Sun on the surface */}
          <div className="absolute -top-16 left-1/2 h-40 w-[110%] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(210,248,255,0.55),rgba(150,230,255,0.18)_55%,transparent)]" />
          <Water id={id} />

          <FishSchool />
          <Diver id={id} />
          {/* Bubble trail from the regulator */}
          <div className="absolute top-[104px] left-[calc(50%-41px)]">
            {BUBBLES.map((bubble) => (
              <span
                key={bubble.delay}
                className="scuba-bubble absolute rounded-full border border-white/70 bg-white/15"
                style={{
                  width: bubble.size,
                  height: bubble.size,
                  left: bubble.x,
                  top: bubble.y,
                  animationDelay: bubble.delay,
                }}
              />
            ))}
          </div>

          <LeftReef id={id} />
          <RightReef id={id} />
        </>
      }
      overlays={
        <div className="pointer-events-none absolute inset-0 z-10 bg-[radial-gradient(ellipse_110%_120%_at_50%_30%,transparent_45%,rgba(1,14,22,0.6)_100%)]" />
      }
    />
  );
}
